import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { MDButton } from '../../../components/MDButton';
import { MDTypography } from '../../../components/MDTypography';
import { MDInput } from '../../../components/MDInput';
import { Bill } from '../ClaimDetailsTypes';
import {
  formatCurrency,
  getVerdictEditOptions,
  validateAmount,
} from '../utils/billActionUtils';
import ConfirmationDialog from './ConfirmationDialog';
import { BillVerdictStatus } from '../../../types';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 500,
      maxWidth: 650,
      padding: theme.spacing(2),
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 250,
  },
  billInfo: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  billInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  currentVerdict: {
    backgroundColor: theme.palette.info.light,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  radioGroup: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  amountInput: {
    marginTop: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
}));

interface VerdictEditModalProps {
  open: boolean;
  bill: Bill | null;
  loading: boolean;
  onClose: () => void;
  onSave: (
    billId: number,
    verdict: BillVerdictStatus,
    amount: number,
    reason: string
  ) => void;
}

export const VerdictEditModal: React.FC<VerdictEditModalProps> = ({
  open,
  bill,
  loading,
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [selectedVerdict, setSelectedVerdict] =
    useState<BillVerdictStatus>('approved');
  const [approveFullBill, setApproveFullBill] = useState(true);
  const [customAmount, setCustomAmount] = useState('');
  const [reason, setReason] = useState('');
  const [amountError, setAmountError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Track original values for change detection
  const [originalValues, setOriginalValues] = useState({
    verdict: '',
    amount: 0,
    reason: '',
  });

  const editOptions = bill?.verdict
    ? getVerdictEditOptions(bill.verdict.verdict)
    : getVerdictEditOptions();

  useEffect(() => {
    if (open && bill) {
      // Set original values
      const original = {
        verdict: bill.verdict?.verdict || '',
        amount: bill.verdict?.amount || 0,
        reason: bill.verdict?.reason || '',
      };
      setOriginalValues(original);

      // Reset form when modal opens
      setSelectedVerdict(bill.verdict?.verdict || 'approved');
      setApproveFullBill(true);
      setCustomAmount('');
      setReason(original.reason);
      setAmountError('');
      setShowConfirmation(false);
    }
  }, [open, bill]);

  useEffect(() => {
    // Update reason text based on verdict selection
    if (selectedVerdict === 'approved') {
      setReason('Approved full billed amount');
      setApproveFullBill(true);
    } else if (selectedVerdict === 'rejected') {
      setReason('');
    } else if (selectedVerdict === 'partial-approved') {
      setReason('Approved partial amount');
      setApproveFullBill(false);
    }
  }, [selectedVerdict]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return;
    }

    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      return;
    }

    setCustomAmount(numericValue);
    if (numericValue && bill) {
      const validation = validateAmount(numericValue, bill.billedCharges);
      setAmountError(validation.error || '');
    } else {
      setAmountError('');
    }
  };

  const hasFormChanged = (): boolean => {
    if (!bill) return false;

    const currentVerdict = selectedVerdict;
    const currentReason = reason.trim();
    let currentAmount = bill.billedCharges;

    if (selectedVerdict === 'partial-approved' && !approveFullBill) {
      currentAmount = parseFloat(customAmount) || 0;
    } else if (selectedVerdict === 'rejected') {
      currentAmount = 0;
    }

    return (
      originalValues.verdict !== currentVerdict ||
      originalValues.amount !== currentAmount ||
      originalValues.reason !== currentReason
    );
  };

  const handleSave = async () => {
    if (!bill) return;

    let amount = bill.billedCharges;

    if (selectedVerdict === 'partial-approved') {
      if (!approveFullBill) {
        const validation = validateAmount(customAmount, bill.billedCharges);
        if (!validation.isValid) {
          setAmountError(validation.error || '');
          return;
        }
        amount = parseFloat(customAmount);
      }
    } else if (selectedVerdict === 'rejected') {
      amount = 0;
    }

    if (!reason.trim() && selectedVerdict !== 'pending') {
      return;
    }

    await onSave(bill.id, selectedVerdict, amount, reason.trim());
  };

  const handleClose = () => {
    if (hasFormChanged()) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const isFormValid = () => {
    if (!reason.trim()) return false;
    if (selectedVerdict === 'partial-approved' && !approveFullBill) {
      return customAmount && !amountError;
    }
    return true;
  };

  if (!bill) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={classes.title}>
          <MDTypography variant="h6" fontWeight="medium">
            {editOptions.title} - {bill.gemBillNo}
          </MDTypography>
        </DialogTitle>

        <DialogContent className={classes.content}>
          {/* Bill Information */}
          <Box className={classes.billInfo}>
            <div className={classes.billInfoRow}>
              <MDTypography variant="body2" color="text">
                Bill ID:
              </MDTypography>
              <MDTypography variant="body2" fontWeight="medium">
                {bill.gemBillNo}
              </MDTypography>
            </div>
            <div className={classes.billInfoRow}>
              <MDTypography variant="body2" color="text">
                Provider:
              </MDTypography>
              <MDTypography variant="body2" fontWeight="medium">
                {bill.payableTo}
              </MDTypography>
            </div>
            <div className={classes.billInfoRow}>
              <MDTypography variant="body2" color="text">
                Bill Amount:
              </MDTypography>
              <MDTypography variant="body2" fontWeight="medium">
                {formatCurrency(bill.billedCharges)}
              </MDTypography>
            </div>
          </Box>

          {/* Current Verdict */}
          {bill.verdict && (
            <Box className={classes.currentVerdict}>
              <MDTypography
                variant="body2"
                fontWeight="medium"
                style={{ marginBottom: '8px' }}
              >
                Current Status:
              </MDTypography>
              <div className={classes.billInfoRow}>
                <MDTypography variant="body2" color="text">
                  Verdict:
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {bill.verdict.verdict}
                  {bill.verdict.verdict === 'partial-approved' &&
                    ` (${formatCurrency(bill.verdict.amount)})`}
                </MDTypography>
              </div>
              {bill.verdict.reason && (
                <div className={classes.billInfoRow}>
                  <MDTypography variant="body2" color="text">
                    Reason:
                  </MDTypography>
                  <MDTypography variant="body2">
                    {bill.verdict.reason}
                  </MDTypography>
                </div>
              )}
            </Box>
          )}

          {/* Edit Options */}
          <MDTypography
            variant="body2"
            fontWeight="medium"
            style={{ marginBottom: '12px' }}
          >
            {editOptions.description}
          </MDTypography>

          <RadioGroup
            value={selectedVerdict}
            onChange={(e) => setSelectedVerdict(e.target.value as any)}
            className={classes.radioGroup}
          >
            {editOptions.options.includes('approved') && (
              <FormControlLabel
                value="approved"
                control={<Radio color="primary" />}
                label={
                  <MDTypography variant="body2">
                    Approve Full Bill ({formatCurrency(bill.billedCharges)})
                  </MDTypography>
                }
              />
            )}
            {editOptions.options.includes('partial-approved') && (
              <FormControlLabel
                value="partial-approved"
                control={<Radio color="primary" />}
                label={
                  <MDTypography variant="body2">Partial Approval</MDTypography>
                }
              />
            )}
            {editOptions.options.includes('reject') && (
              <FormControlLabel
                value="rejected"
                control={<Radio color="primary" />}
                label={<MDTypography variant="body2">Reject Bill</MDTypography>}
              />
            )}
            <FormControlLabel
              value="pending"
              control={<Radio color="primary" />}
              label={
                <MDTypography variant="body2">
                  Undo current verdict (sets to Pending)
                </MDTypography>
              }
            />
          </RadioGroup>

          {/* Partial Approval Amount Input */}
          {selectedVerdict === 'partial-approved' && (
            <Box className={classes.amountInput}>
              <MDInput
                label="Custom Approval Amount (USD)"
                value={customAmount}
                onChange={handleAmountChange}
                placeholder="0.00"
                fullWidth
                error={!!amountError}
              />
              {amountError ? (
                <MDTypography
                  variant="caption"
                  color="error"
                  style={{ marginTop: '4px', display: 'block' }}
                >
                  {amountError}
                </MDTypography>
              ) : (
                <MDTypography
                  variant="caption"
                  color="text"
                  style={{ marginTop: '4px', display: 'block' }}
                >
                  Maximum: {formatCurrency(bill.billedCharges)}
                </MDTypography>
              )}
            </Box>
          )}

          {/* Reason Input */}
          <MDInput
            label="Reason / Comments"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Enter reason or comments..."
          />
        </DialogContent>

        <DialogActions className={classes.actions}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleSave}
            disabled={loading || !isFormValid() || !hasFormChanged()}
          >
            {loading ? 'Processing...' : 'Save'}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={showConfirmation}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Yes, Close"
        cancelText="Continue Editing"
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
};

export default VerdictEditModal;
